import { ResourceClient, type HttpClient } from "@dl/web-core-js/api";
import type { Currency, Period, SigmClientDTO } from "./reporting";

export interface OrderLineDTO {
  id: number;
  line_no?: number | null;
  client_line_no?: string | null;
  part?: { no?: string | null; description1?: string | null } | null;
  part_upc?: string | null;
  price?: number | null;
  ordered_qty?: number | null;
  ship_qty?: number | null;
  cancel_qty?: number | null;
  reserved_qty?: number | null;
  requested_date?: string | null;
  warehouse?: string | null;
  active?: string | boolean;
}

export interface OrderDTO {
  id: number;
  no?: string | null;
  original_order_no?: number | null;
  date?: string | null;
  timestamp?: string | null;
  status?: string | null;
  order_type?: number | null;
  currency?: Currency | null;
  warehouse?: string | null;
  client?: SigmClientDTO | null;
  client_order_no?: string | null;
  deposit?: number | null;
  carrier?: string | null;
  carrier_scac?: string | null;
  cancel_date?: string | null;
  expiry_date?: string | null;
  ship_name?: string | null;
  ship_address?: string | null;
  /** SIGM's second ship-to line (name continuation): apartment, suite, c/o. */
  ship_name2?: string | null;
  ship_city?: string | null;
  ship_postal_code?: string | null;
  ship_phone?: string | null;
  ship_email?: string | null;
  note1?: string | null;
  note2?: string | null;
  note3?: string | null;
  note4?: string | null;
  line_count_header?: number | null;
  lines?: OrderLineDTO[];
}

/** A line's open quantity: ordered minus shipped minus cancelled. */
export function openQty(line: OrderLineDTO): number {
  return (line.ordered_qty ?? 0) - (line.ship_qty ?? 0) - (line.cancel_qty ?? 0);
}

/** `getLineTotal()`: gross line value, price × ordered quantity. */
export function orderLineTotal(line: OrderLineDTO): number {
  return (line.price ?? 0) * (line.ordered_qty ?? 0);
}

/** The USD retail partners the Deringer 3PL feed covers — fixed business list. */
export const DERINGER_CLIENT_IDS = [163, 775, 551, 20, 834];

export const DERINGER_PERIODS = [
  "TODAY",
  "TODAY_YESTERDAY",
  "THIS_WEEK",
  "LAST_7_DAYS",
  "LAST_14_DAYS",
  "LAST_30_DAYS",
] as const;
export type DeringerPeriod = (typeof DERINGER_PERIODS)[number];

/** Resolves a preset to concrete dates, mirroring the Vaadin `LocalDateUtil` mapping. */
export function deringerPeriodDates(preset: DeringerPeriod, today = new Date()): Period {
  const iso = (date: Date) => date.toISOString().slice(0, 10);
  const daysAgo = (days: number) => {
    const date = new Date(today);
    date.setDate(date.getDate() - days);
    return date;
  };
  switch (preset) {
    case "TODAY":
      return { from: iso(today), to: iso(today) };
    case "TODAY_YESTERDAY":
      return { from: iso(daysAgo(1)), to: iso(today) };
    case "THIS_WEEK": {
      const monday = new Date(today);
      monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
      return { from: iso(monday), to: iso(today) };
    }
    case "LAST_7_DAYS":
      return { from: iso(daysAgo(7)), to: iso(today) };
    case "LAST_14_DAYS":
      return { from: iso(daysAgo(14)), to: iso(today) };
    case "LAST_30_DAYS":
      return { from: iso(daysAgo(30)), to: iso(today) };
  }
}

export class OrderClient {
  constructor(private readonly http: HttpClient) {}

  byPeriod(period: Period): Promise<OrderDTO[]> {
    return this.http.get<OrderDTO[]>("/reporting/order/period", {
      query: { date_from: period.from, date_to: period.to },
    });
  }

  detail(orderId: number): Promise<OrderDTO> {
    return this.http.get<OrderDTO>(`/reporting/order/detail/${orderId}`);
  }

  /** Deringer CSV for explicitly selected orders. */
  documentDeringer(orderIds: number[]) {
    return this.http.job("/reporting/order/document/deringer", { body: orderIds });
  }

  /** Deringer CSV for the partner clients over a date range. */
  /**
   * Uploads the 3PL's shipment report — their Ship Report `.csv` or the older
   * Customer Small Parcel Report `.xlsx`, the server sniffs which — and gets
   * back a flat CSV of tracking numbers for the SIGM import macro. Not a job:
   * it is a parse of a few dozen rows and the operator is waiting on it.
   */
  trackingFromParcelReport(file: File) {
    return this.http.download("/reporting/order/document/deringer/tracking", { raw: file });
  }

  documentDeringerForPeriod(clientIds: number[], period: Period, removeHeader: boolean) {
    return this.http.job("/reporting/order/document/deringer/period", {
      body: clientIds,
      query: { date_from: period.from, date_to: period.to, remove_header: removeHeader },
    });
  }
}

export class SigmClientClient extends ResourceClient<SigmClientDTO> {
  constructor(http: HttpClient) {
    super(http, "/client");
  }
}
